import { create } from 'zustand';
import { User } from 'firebase/auth';
import { TelegramAccountEntity } from '../../domain/entities/TelegramAccount';
import { useCases } from '../../infrastructure/di/container';

type Pagination = { page: number; perPage: number; total: number };

type State = {
  items: TelegramAccountEntity[];
  pagination: Pagination;
  isLoading: boolean;
  error: string | null;
  search: string;
  isFetchingMore: boolean;
}

type Actions = {
  list: (user: User, page: number, perPage: number, search?: string) => Promise<void>;
  setSearch: (q: string) => void;
  updateStatus: (user: User, uuid: string, isActive: boolean) => Promise<boolean>;
  remove: (user: User, uuid: string) => Promise<boolean>;
  startAuth: (user: User, uuid: string) => Promise<boolean>;
  finishAuth: (user: User, uuid: string, code: string, password: string | null) => Promise<boolean>;
  reset: () => void;
}

const initial: State = { items: [], pagination: { page: 1, perPage: 12, total: 0 }, isLoading: false, error: null, search: '', isFetchingMore: false };

export const useTelegramAccountsStore = create<State & Actions>((set, get) => ({
  ...initial,
  async list(user, page, perPage, search) {
    const currentSearch = search ?? get().search;
    const previousSearch = get().search;
    const isNextPage = page > 1 && currentSearch === previousSearch;
    if ((get().isLoading && !isNextPage) || get().isFetchingMore) return;
    set({ isLoading: !isNextPage, isFetchingMore: isNextPage, error: null, search: currentSearch });
    try {
      const res = await useCases.listTelegramAccounts().execute({ page, perPage, search: currentSearch });
      set((state) => ({
        items: isNextPage ? [...state.items, ...res.items] : res.items,
        pagination: { page: res.page, perPage: res.perPage, total: res.total },
        isLoading: false,
        isFetchingMore: false,
      }));
    } catch (e: any) {
      set({ isLoading: false, isFetchingMore: false, error: e?.message || 'Failed to load telegram accounts' });
    }
  },
  setSearch(q) { set({ search: q }); },
  async updateStatus(user, uuid, isActive) {
    set({ isLoading: true, error: null });
    try {
      const updated = await useCases.updateTelegramAccount().execute(uuid, { status: isActive ? 'ACTIVE' : 'INACTIVE' } as any);
      set({ items: get().items.map(i => i.uuid === uuid ? updated : i), isLoading: false });
      return true;
    } catch (e: any) {
      set({ isLoading: false, error: e?.message || 'Failed to update account' });
      return false;
    }
  },
  async remove(user, uuid) {
    set({ isLoading: true, error: null });
    try {
      await useCases.deleteTelegramAccount().execute(uuid);
      set({ items: get().items.filter(i => i.uuid !== uuid), isLoading: false });
      return true;
    } catch (e: any) {
      set({ isLoading: false, error: e?.message || 'Failed to delete account' });
      return false;
    }
  },
  async startAuth(user, uuid) {
    set({ isLoading: true, error: null });
    try {
      await useCases.startTelegramAccountAuth().execute(uuid);
      set({ isLoading: false });
      return true;
    } catch (e: any) {
      set({ isLoading: false, error: e?.message || 'Failed to start auth' });
      return false;
    }
  },
  async finishAuth(user, uuid, code, password) {
    set({ isLoading: true, error: null });
    try {
      await useCases.finishTelegramAccountAuth().execute(uuid, { code, password });
      set({ items: get().items.map(i => i.uuid === uuid ? i.withStatus('AUTHENTICATED') : i), isLoading: false });
      return true;
    } catch (e: any) {
      set({ isLoading: false, error: e?.message || 'Failed to finish auth' });
      return false;
    }
  },
  reset() { set(initial); }
}));
